import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  User, 
  Phone, 
  Package, 
  Calendar, 
  DollarSign, 
  Truck, 
  Save, 
  Printer, 
  RefreshCw,
  Plus,
  Trash2,
  RotateCcw
} from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import CustomerAutoComplete from './CustomerAutoComplete';
import { getConfig } from '../services/config';

const EDITABLE_ORDER_STATUS_KEYWORDS = ['PKS', 'PKD', 'PKR', 'INS', 'RTN', 'INT'];

const resolveCustomerAddressId = (c) => {
  if (!c) return 0;
  const id =
    c.CustomerAddressId ??
    c.DefaultCustomerAddressId ??
    c.DefaultAddressId ??
    c.AddressId ??
    c.CustomerAddress?.Id;
  return id != null && id !== '' ? Number(id) : 0;
};

const normalizeChargeFrom = (cf) => {
  if (!cf) return 'CLIENT';
  const s = String(cf).toUpperCase();
  if (s === 'AREA') return 'AREA';
  return 'CLIENT';
};

const parseApiDateToInput = (d) => {
  if (!d) return new Date().toISOString().split('T')[0];
  if (typeof d === 'string' && d.includes('/Date(')) {
    const m = /\/Date\((\d+)\)\//.exec(d);
    if (m) return new Date(parseInt(m[1], 10)).toISOString().split('T')[0];
  }
  const dt = new Date(d);
  return !isNaN(dt.getTime()) ? dt.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
};

const NewOrder = () => {
  const { orderId: editOrderIdParam } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    pickupAddress: '',
    clientBillNo: '',
    voucherNo: '',
    type: '',
    payType: 'CAD',
    customer: '',
    customerMobile: '',
    customerMobile2: '',
    emirate: '',
    location: '',
    poBox: '',
    address: '',
    quantity: 1,
    remarks: '',
    pickupDate: '',
    deliveryDate: '',
    productPrice: 0,
    forPickup: true,
    exchange: false,
    fragile: false,
    customerId: 0,
    customerAddressId: 0
  });

  const [customerAutocompleteKey, setCustomerAutocompleteKey] = useState(0);
  const [addresses, setAddresses] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [types, setTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedClient, setSelectedClient] = useState({
    MobileNo1: '',
    Id: '',
    Name: '',
    AddressId: ''
  });
  const [orderSnapshot, setOrderSnapshot] = useState(null);
  const { showSuccess, showError, showWarning } = useToast();

  const isEditMode = Boolean(editOrderIdParam);

  useEffect(() => {
    initializeData();
  }, [editOrderIdParam]);

  const invalidateCustomerIds = () => {
    setFormData(prev => {
      if (prev.customerId === 0 && prev.customerAddressId === 0) return prev;
      return { ...prev, customerId: 0, customerAddressId: 0 };
    });
  };

  const resetCustomerForm = () => {
    setCustomerAutocompleteKey(k => k + 1);
    setFormData(prev => ({
      ...prev,
      customer: '',
      customerMobile: '',
      customerMobile2: '',
      emirate: '',
      location: '',
      poBox: '',
      address: '',
      customerId: 0,
      customerAddressId: 0
    }));
  };

  const handleCustomerSelect = (customer) => {
    if (!customer) return;
    const emirateVal =
      customer.EmirateId != null
        ? String(customer.EmirateId)
        : customer.Emirate?.Id != null
          ? String(customer.Emirate.Id)
          : '';
    setFormData(prev => ({
      ...prev,
      customer: customer.Name || '',
      customerMobile: customer.MobileNo1 || '',
      customerMobile2: customer.MobileNo2 || '',
      emirate: emirateVal || prev.emirate,
      location: customer.LocationName || customer.Location?.Name || '',
      address: customer.Address || '',
      poBox: customer.POBOX || customer.POBox || '',
      customerId: customer.Id != null ? Number(customer.Id) : 0,
      customerAddressId: resolveCustomerAddressId(customer)
    }));
  };

  const loadOrderForEdit = async (orderId, clientData, token) => {
    try {
      const order = await MainService.OrderById(orderId, token);
      if (!order || !order.Id) {
        showError('Order could not be loaded.');
        navigate('/deliveries');
        return;
      }
      if (Number(order.ClientId) !== Number(clientData.Id)) {
        showError('You can only edit your own orders.');
        navigate('/deliveries');
        return;
      }
      const kw = order.Status?.Keyword;
      if (kw && !EDITABLE_ORDER_STATUS_KEYWORDS.includes(kw)) {
        showWarning('Cannot Update Order Because Its In Processing Stage');
        navigate('/deliveries');
        return;
      }

      const addrId = order.ClientAddressId != null ? String(order.ClientAddressId) : '';
      setSelectedClient(prev => ({ ...prev, AddressId: addrId }));
      setOrderSnapshot(order);

      setFormData(prev => ({
        ...prev,
        pickupAddress: addrId,
        clientBillNo: order.ClientBillNo || '',
        voucherNo: order.VoucherNo || '',
        type: order.Type?.Name || '',
        payType: order.PayType || 'CAD',
        customer: order.Customer?.Name || '',
        customerMobile: order.Customer?.MobileNo1 || '',
        customerMobile2: order.Customer?.MobileNo2 || '',
        emirate: order.CustomerAddress?.EmirateId != null ? String(order.CustomerAddress.EmirateId) : '',
        location: order.CustomerAddress?.Location?.Name || '',
        poBox: order.CustomerAddress?.POBOX || '',
        address: order.CustomerAddress?.Address || '',
        quantity: order.Quantity != null ? order.Quantity : 1,
        remarks: order.Remarks || '',
        pickupDate: parseApiDateToInput(order.PickupDate),
        deliveryDate: parseApiDateToInput(order.DeliveryDate),
        productPrice: order.ProductPrice != null ? order.ProductPrice : 0,
        forPickup: Boolean(order.IsPickup),
        exchange: Boolean(order.IsExchange),
        fragile: Boolean(order.IsFragile),
        customerId: order.CustomerId != null ? Number(order.CustomerId) : 0,
        customerAddressId: order.CustomerAddressId != null ? Number(order.CustomerAddressId) : 0
      }));
      setCustomerAutocompleteKey(k => k + 1);
    } catch (err) {
      console.error('Error loading order for edit:', err);
      showError('Failed to load order.');
      navigate('/deliveries');
    }
  };

  const initializeData = async () => {
    try {
      setIsLoading(true);
      setOrderSnapshot(null);

      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (!editOrderIdParam) {
        setCustomerAutocompleteKey(k => k + 1);
        setFormData({
          pickupAddress: '',
          clientBillNo: '',
          voucherNo: '',
          type: '',
          payType: 'CAD',
          customer: '',
          customerMobile: '',
          customerMobile2: '',
          emirate: '',
          location: '',
          poBox: '',
          address: '',
          quantity: 1,
          remarks: '',
          pickupDate: today.toISOString().split('T')[0],
          deliveryDate: tomorrow.toISOString().split('T')[0],
          productPrice: 0,
          forPickup: true,
          exchange: false,
          fragile: false,
          customerId: 0,
          customerAddressId: 0
        });
        setSelectedClient(prev => ({
          ...prev,
          AddressId: ''
        }));
      }

      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      let clientData = null;
      let token = '';

      if (tokenData) {
        const userData = JSON.parse(tokenData);
        clientData = {
          MobileNo1: userData.MobileNo1,
          Id: userData.ClientId,
          Name: userData.Name,
          Token: userData.Token,
          AddressId: ''
        };
        token = userData.Token;
        setSelectedClient(clientData);

        try {
          const addressesResponse = await MainService.GetClientAddress(clientData.Id);
          setAddresses(addressesResponse || []);
        } catch (error) {
          console.error('Error loading addresses:', error);
          setAddresses([]);
          showError('Failed to load pickup addresses');
        }
      }

      try {
        const emiratesResponse = await MainService.EmiratesInDropDown();
        setEmirates(emiratesResponse || []);
      } catch (error) {
        console.error('Error loading emirates:', error);
        setEmirates([]);
        showError('Failed to load emirates');
      }

      let typesResponse = [];
      try {
        typesResponse = await MainService.TypesInDropDown();
        setTypes(typesResponse || []);
      } catch (error) {
        console.error('Error loading types:', error);
        setTypes([]);
        showError('Failed to load types');
      }

      try {
        const profileResp = await MainService.GetCompanyProfile();
        const companyProfile = profileResp?.data ?? profileResp ?? {};
        const defaultTypeId = companyProfile.DefaultTypeId;
        if (
          !editOrderIdParam &&
          defaultTypeId != null &&
          defaultTypeId !== '' &&
          typesResponse.length > 0
        ) {
          const defType = typesResponse.find(
            (t) => Number(t.Id) === Number(defaultTypeId)
          );
          if (defType?.Name) {
            setFormData((prev) => ({ ...prev, type: defType.Name }));
          }
        }
      } catch (error) {
        console.error('Error loading company profile:', error);
      }

      if (editOrderIdParam && clientData && token) {
        await loadOrderForEdit(editOrderIdParam, clientData, token);
      } else if (!editOrderIdParam) {
        try {
          const orderNoResponse = await MainService.GetNextOrderNo();
          setFormData(prev => ({
            ...prev,
            voucherNo: orderNoResponse || ''
          }));
        } catch (error) {
          console.error('Error loading next order number:', error);
          showError('Failed to load next order number');
        }
      }

    } catch (error) {
      console.error('Error initializing data:', error);
      showError('Failed to load initial data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotals = () => {
    const subtotal = parseFloat(formData.productPrice);
    const tax = subtotal * 0.05;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission
  };

  const transformOrderData = () => {
    const pickupDate = new Date(formData.pickupDate);
    const deliveryDate = new Date(formData.deliveryDate);
    
    // Find selected type ID
    const selectedType = types.find(type => type.Name === formData.type);
    
    const typeId = selectedType?.Id != null ? Number(selectedType.Id) : 0;
    return {
      VoucherNo: formData.voucherNo,
      ClientBillNo: formData.clientBillNo,
      ChargeFrom: 'CLIENT',
      PayType: formData.payType,
      CustomerName: formData.customer,
      MobileNo: formData.customerMobile,
      MobileNo2: formData.customerMobile2 || formData.customerMobile,
      Location: formData.location,
      Address: formData.address,
      Remarks: formData.remarks,
      Quantity: formData.quantity,
      ProductPrice: formData.productPrice,
      ClientId: selectedClient.Id,
      EmirateId: formData.emirate,
      ClientAddressId: selectedClient.AddressId,
      TypeId: typeId,
      IsPickup: formData.forPickup,
      IsFragile: formData.fragile,
      IsExchange: formData.exchange,
      CustomerId: Number(formData.customerId) || 0,
      CustomerAddressId: Number(formData.customerAddressId) || 0,
      PMonth: pickupDate.getMonth() + 1,
      PDay: pickupDate.getDate(),
      PYear: pickupDate.getFullYear(),
      DMonth: deliveryDate.getMonth() + 1,
      DDay: deliveryDate.getDate(),
      DYear: deliveryDate.getFullYear()
    };
  };

  /** OrderUploadDto shape for UpdatePortalOrder */
  const buildPortalUpdatePayload = () => {
    const pickupDate = new Date(formData.pickupDate);
    const deliveryDate = new Date(formData.deliveryDate);
    const selectedType = types.find(type => type.Name === formData.type);
    const typeId =
      selectedType?.Id != null
        ? Number(selectedType.Id)
        : orderSnapshot.TypeId != null
          ? Number(orderSnapshot.TypeId)
          : 0;

    return {
      Id: Number(orderSnapshot.Id),
      VoucherNo: formData.voucherNo,
      ClientBillNo: formData.clientBillNo,
      ChargeFrom: normalizeChargeFrom(orderSnapshot.ChargeFrom),
      PayType: formData.payType,
      Remarks: formData.remarks,
      Quantity: formData.quantity,
      ProductPrice: Number(formData.productPrice) || 0,
      CustomerName: formData.customer,
      MobileNo: formData.customerMobile,
      MobileNo2: formData.customerMobile2 || formData.customerMobile,
      Location: formData.location,
      Address: formData.address,
      EmirateId: parseInt(formData.emirate, 10) || 0,
      ClientId: Number(selectedClient.Id),
      ClientAddressId: parseInt(selectedClient.AddressId, 10) || 0,
      TypeId: typeId,
      IsPickup: formData.forPickup,
      IsExchange: formData.exchange,
      IsFragile: formData.fragile,
      CustomerId: Number(formData.customerId) || 0,
      CustomerAddressId: Number(formData.customerAddressId) || 0,
      PMonth: pickupDate.getMonth() + 1,
      PDay: pickupDate.getDate(),
      PYear: pickupDate.getFullYear(),
      DMonth: deliveryDate.getMonth() + 1,
      DDay: deliveryDate.getDate(),
      DYear: deliveryDate.getFullYear()
    };
  };

  const validateForm = () => {
    if (!formData.pickupAddress) {
      showError('Please select a pickup address');
      return false;
    }
    if (!formData.customer) {
      showError('Please enter customer name');
      return false;
    }
    if (!formData.customerMobile) {
      showError('Please enter customer mobile number');
      return false;
    }
    if (!formData.emirate) {
      showError('Please select an emirate');
      return false;
    }
    if (!formData.location) {
      showError('Please enter location');
      return false;
    }
    if (!formData.address) {
      showError('Please enter address');
      return false;
    }
    if (!formData.type) {
      showError('Please select a type');
      return false;
    }
    if (formData.quantity <= 0) {
      showError('Please enter a valid quantity');
      return false;
    }
    if (formData.productPrice < 0) {
      showError('Please enter a valid product price');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (orderSnapshot?.Id > 0) {
      const kw = orderSnapshot.Status?.Keyword;
      if (kw && !EDITABLE_ORDER_STATUS_KEYWORDS.includes(kw)) {
        showWarning('Cannot Update Order Because Its In Processing Stage');
        return;
      }
    }

    try {
      setIsSaving(true);

      if (orderSnapshot?.Id > 0) {
        const payload = buildPortalUpdatePayload();
        const response = await MainService.UpdatePortalOrder(payload, selectedClient.Token);
        if (response.Status === 'Success') {
          showSuccess('Order updated successfully!');
          navigate('/deliveries');
        } else {
          showError(response.Status || response.Message || 'Failed to update order');
        }
      } else {
        const orderData = transformOrderData();
        const response = await MainService.SavePortalOrder(orderData, selectedClient.Token);
        if (response.Status === 'Success') {
          showSuccess('Order saved successfully!');
          handleRefresh();
        } else {
          showError(response.Message || 'Failed to save order');
        }
      }
    } catch (error) {
      console.error('Error saving order:', error);
      showError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndPrint = async () => {
    if (!validateForm()) {
      return;
    }

    if (orderSnapshot?.Id > 0) {
      const kw = orderSnapshot.Status?.Keyword;
      if (kw && !EDITABLE_ORDER_STATUS_KEYWORDS.includes(kw)) {
        showWarning('Cannot Update Order Because Its In Processing Stage');
        return;
      }
    }

    try {
      setIsSaving(true);

      if (orderSnapshot?.Id > 0) {
        const payload = buildPortalUpdatePayload();
        const response = await MainService.UpdatePortalOrder(payload, selectedClient.Token);
        if (response.Status === 'Success') {
          showSuccess('Order updated successfully!');
          const printId =
            (response.OrderIds && response.OrderIds[0]) || response.OrderId || orderSnapshot.Id;
          if (printId) await handlePrintOrder(printId);
          navigate('/deliveries');
        } else {
          showError(response.Status || response.Message || 'Failed to update order');
        }
      } else {
        const orderData = transformOrderData();
        const response = await MainService.SavePortalOrder(orderData, selectedClient.Token);
        if (response.Status === 'Success') {
          showSuccess('Order saved successfully!');
          if (response.OrderIds != null) {
            await handlePrintOrder(response.OrderIds[0]);
          } else {
            showError('Order ID not found in response');
          }
          handleRefresh();
        } else {
          showError(response.Message || 'Failed to save order');
        }
      }
    } catch (error) {
      console.error('Error saving order:', error);
      showError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrintOrder = async (orderId) => {
    try {
      setIsPrinting(true);
      const response = await MainService.PrintOrder(orderId);
      
      if (response) {
        // Open the PDF in a new tab
        const pdfUrl = `${getConfig().API_BASE_URL}ReportFiles/${response}`;
        window.open(pdfUrl, '_blank');
        showSuccess('PDF generated successfully');
      } else {
        showError('Print Error - No data received');
      }
    } catch (error) {
      console.error('Print Error:', error);
      showError('Print Error - Failed to generate PDF');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleRefresh = async () => {
    if (isEditMode) {
      navigate('/new-order', { replace: true });
      return;
    }
    setOrderSnapshot(null);
    setCustomerAutocompleteKey(k => k + 1);
    setFormData({
      pickupAddress: '',
      clientBillNo: '',
      voucherNo: '',
      type: '',
      payType: 'CAD',
      customer: '',
      customerMobile: '',
      customerMobile2: '',
      emirate: '',
      location: '',
      poBox: '',
      address: '',
      quantity: 1,
      remarks: '',
      pickupDate: new Date().toISOString().split('T')[0],
      deliveryDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      productPrice: 0,
      forPickup: true,
      exchange: false,
      fragile: false,
      customerId: 0,
      customerAddressId: 0
    });
    setSelectedClient(prev => ({ ...prev, AddressId: '' }));

    try {
      const orderNoResponse = await MainService.GetNextOrderNo();
      setFormData(prev => ({
        ...prev,
        voucherNo: orderNoResponse || ''
      }));
    } catch (error) {
      console.error('Error loading next order number:', error);
      showError('Failed to load next order number');
    }

    initializeData();
  };

  const { subtotal, tax, total } = calculateTotals();

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-primary-600">Loading pickup addresses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditMode ? 'Edit Delivery Order' : 'New Delivery Order'}
              </h1>
              <p className="text-gray-600 mt-2">
                {isEditMode
                  ? 'Update order details and save changes'
                  : 'Create a new delivery order for your client'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefresh}
                className="btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Order Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Pickup Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 text-primary-600 mr-2" />
                  Pickup Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pickup Address
                    </label>
                    <select
                      value={formData.pickupAddress}
                      onChange={(e) => {
                        setFormData({...formData, pickupAddress: e.target.value});
                        setSelectedClient(prev => ({ ...prev, AddressId: e.target.value }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={addresses.length === 0}
                    >
                      <option value="">
                        {addresses.length === 0 ? 'No addresses available' : 'Choose Pickup Address'}
                      </option>
                      {addresses.map(addr => (
                        <option key={addr.Id} value={String(addr.Id)}>
                          {addr.Address} - {addr.Location?.Name} - {addr.Emirate?.Name}
                        </option>
                      ))}
                    </select>
                    {addresses.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">Please contact support to add addresses</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pickup Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={formData.pickupDate}
                        onChange={(e) => setFormData({...formData, pickupDate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="w-5 h-5 text-primary-600 mr-2" />
                  Order Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Bill No
                    </label>
                    <input
                      type="text"
                      value={formData.clientBillNo}
                      onChange={(e) => setFormData({...formData, clientBillNo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter bill number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Voucher No
                    </label>
                    <input
                      type="text"
                      value={formData.voucherNo}
                      onChange={(e) => setFormData({...formData, voucherNo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter voucher number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={types.length === 0}
                    >
                      <option value="">
                        {types.length === 0 ? 'Loading types...' : 'Select Type'}
                      </option>
                      {types.map(type => (
                        <option key={type.Id} value={type.Name}>{type.Name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <User className="w-5 h-5 text-primary-600 mr-2" />
                    Customer Information
                  </h2>
                  <button
                    type="button"
                    onClick={resetCustomerForm}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-primary-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                    title="Clear customer fields"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset customer
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name
                    </label>
                    <CustomerAutoComplete
                      key={`customer-ac-${customerAutocompleteKey}`}
                      value={formData.customer}
                      onChange={(value) => setFormData(prev => ({ ...prev, customer: value }))}
                      onCustomerSelect={handleCustomerSelect}
                      onInvalidateCustomerIds={invalidateCustomerIds}
                      placeholder="Search customer name..."
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      value={formData.customerMobile}
                      onChange={(e) => setFormData({...formData, customerMobile: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter mobile number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number 2
                    </label>
                    <input
                      type="tel"
                      value={formData.customerMobile2}
                      onChange={(e) => setFormData({...formData, customerMobile2: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Optional second number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emirate
                    </label>
                    <select
                      value={formData.emirate}
                      onChange={(e) => setFormData({...formData, emirate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={emirates.length === 0}
                    >
                      <option value="">
                        {emirates.length === 0 ? 'Loading emirates...' : 'Select Emirate'}
                      </option>
                      {emirates.map(emirate => (
                        <option key={emirate.Id} value={emirate.Id}>{emirate.Name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter location"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PO Box
                    </label>
                    <input
                      type="text"
                      value={formData.poBox}
                      onChange={(e) => setFormData({...formData, poBox: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter PO Box"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter complete address"
                  />
                </div>
              </div>

              {/* Additional Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Truck className="w-5 h-5 text-primary-600 mr-2" />
                  Additional Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pay Type
                    </label>
                    <select
                      value={formData.payType}
                      onChange={(e) => setFormData({...formData, payType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="CAD">CAD</option>
                      <option value="COD">COD</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remarks
                  </label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter any special instructions or remarks"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Actions */}
            <div className="space-y-6">
              {/* Product Price */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  Product Price
                </h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Price (AED)
                  </label>
                  <input
                    type="number"
                    value={formData.productPrice}
                    onChange={(e) => setFormData({...formData, productPrice: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Options</h2>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.exchange}
                      onChange={(e) => setFormData({...formData, exchange: e.target.checked})}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Exchange?</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.fragile}
                      onChange={(e) => setFormData({...formData, fragile: e.target.checked})}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Fragile?</span>
                  </label>
                </div>
              </div>

              {/* Delivery Date */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery Schedule</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={formData.deliveryDate}
                      onChange={(e) => setFormData({...formData, deliveryDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                      min={formData.pickupDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Order</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAndPrint}
                    disabled={isSaving || isPrinting}
                    className="w-full btn-secondary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : isPrinting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Generating PDF...</span>
                      </>
                    ) : (
                      <>
                        <Printer className="w-4 h-4" />
                        <span>Save & Print</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewOrder;
